/* eslint-disable */
/* eslint-disable no-undef */
import useVirtulization from '@/Helpers/useVirtulization';
import {
	ArrowDown, ArrowUp, ArrowUpDown, Loader2,
	Plus, Minus
} from 'lucide-react';
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';

function VariantTable(props) {
	const {
		data = [],
		columns = [],
		colMapping,
		customRowData = {},
		sortValueInfo = {},
		customCol = () => { },
		highLightRow = null,
		virtualization = false,
		isLoading: externalLoading = false,
		deferRender = false,
		customInfoLine = 'No data available',
		showSubRow = false,
		renderSubRow = () => null,
		headerCellClassName = 'bg-yellow-200',
		headerRowClassName = 'bg-yellow-200',
		groups = {},
	} = props;

	const [sortColumn, setSortColumn] = useState(null);
	const [sortDirection, setSortDirection] = useState('asc');
	const [isScrolled, setIsScrolled] = useState(false);
	const [isInternalLoading, setIsInternalLoading] = useState(false);
	const [deferredData, setDeferredData] = useState(data);
	const [headerHeight, setHeaderHeight] = useState(0);
	const scrollContainerRef = useRef(null);
	const [expandedSubRow, setExpandedSubRow] = useState({});
	const theadRef = useRef(null);
	const groupRowRef = useRef(null);
	const [groupHeaderHeight, setGroupHeaderHeight] = useState(0);
	const dataRef = useRef(data);

	useEffect(() => {
		const updateHeaderHeight = () => {
			if (theadRef.current) {
				requestAnimationFrame(() => {
					if (theadRef.current) {
						setHeaderHeight(theadRef.current.offsetHeight);
					}
				});
			}
		};
		updateHeaderHeight();
		const resizeObserver = new ResizeObserver(updateHeaderHeight);
		if (theadRef.current) {
			resizeObserver.observe(theadRef.current);
		}
		return () => resizeObserver.disconnect();
	}, [columns]);

	const groupSegments = useMemo(() => {
		if (!columns?.some((c) => c?.group)) return [];
		const segments = [];
		columns.forEach((col, index) => {
			const label = col?.group || null;
			const last = segments[segments.length - 1];
			if (last && last.label === label) {
				last.colSpan += 1;
				last.endIndex = index;
				last.fixed = last.fixed && !!col?.fixed;
			} else {
				segments.push({
					label,
					color: col?.groupColor ?? groups?.[label]?.color,
					textColor: col?.groupTextColor ?? groups?.[label]?.textColor,
					subLabel: groups?.[label]?.subLabel,
					colSpan: 1,
					startIndex: index,
					endIndex: index,
					fixed: !!col?.fixed,
				});
			}
		});
		return segments;
	}, [columns, groups]);

	const hasGroups = groupSegments.length > 0;

	// Column indices that sit at the right edge of a group — used to draw a
	// vertical separator between groups (in both the header and the body).
	const groupBoundaryIndices = useMemo(() => {
		const set = new Set();
		if (hasGroups) {
			groupSegments.forEach((seg, i) => {
				if (i < groupSegments.length - 1) set.add(seg.endIndex);
			});
		}
		return set;
	}, [groupSegments, hasGroups]);

	// Keep the title row docked right below the group row when groups exist.
	useEffect(() => {
		if (!hasGroups) {
			setGroupHeaderHeight(0);
			return undefined;
		}
		const updateGroupHeight = () => {
			if (groupRowRef.current) {
				requestAnimationFrame(() => {
					if (groupRowRef.current) {
						setGroupHeaderHeight(groupRowRef.current.offsetHeight);
					}
				});
			}
		};
		updateGroupHeight();
		const resizeObserver = new ResizeObserver(updateGroupHeight);
		if (groupRowRef.current) {
			resizeObserver.observe(groupRowRef.current);
		}
		return () => resizeObserver.disconnect();
	}, [hasGroups, columns]);

	useEffect(() => {
		if (!deferRender) {
			setDeferredData(data);
			return;
		}

		if (dataRef.current !== data) {
			dataRef.current = data;
			setIsInternalLoading(true);
			requestAnimationFrame(() => {
				setTimeout(() => {
					setDeferredData(data);
					requestAnimationFrame(() => {
						setIsInternalLoading(false);
					});
				}, 0);
			});
		}
	}, [data, deferRender]);

	const isLoading = externalLoading || isInternalLoading;
	const activeData = deferRender ? deferredData : data;

	const getSortIcon = (column) => {
		if (!column?.sortable) return null;

		if (sortColumn === column?.key) {
			return sortDirection === 'asc'
				? <ArrowUp size={20} className='cursor-pointer text-blue-600' />
				: <ArrowDown size={20} className='cursor-pointer text-blue-600' />;
		}
		return <ArrowUpDown size={20} className='cursor-pointer text-gray-400 hover:text-gray-600' />;
	};

	const handleSort = (column) => {
		if (!column?.sortable) return;
		setSortColumn(column?.key);
		setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
	};

	const getValueByPath = (obj, path) => {
		const cleanPath = path.replace(/obj\??\./g, '').split('.');
		return cleanPath.reduce((current, key) => current?.[key], obj);
	};

	const sortedData = useMemo(() => {
		if (!sortColumn) return activeData;

		const fixedRows = activeData.filter(row => row?.fixed);
		const nonFixedRows = activeData.filter(row => !row?.fixed);

		const sortedNonFixed = [...nonFixedRows].sort((a, b) => {
			let aValue;
			let bValue;
			let sortType = 'string';

			if (sortValueInfo && sortValueInfo[sortColumn]) {
				const sortInfo = sortValueInfo[sortColumn];
				sortType = sortInfo?.type || 'string';
				if (sortInfo?.customValue) {
					aValue = typeof sortInfo?.value === 'function' ? sortInfo?.value(a) : a[sortInfo?.value];
					bValue = typeof sortInfo?.value === 'function' ? sortInfo?.value(b) : b[sortInfo?.value];
				} else {
					if (sortInfo?.path) {
						aValue = getValueByPath(a, sortInfo?.path);
						bValue = getValueByPath(b, sortInfo?.path);
					} else {
						aValue = a[sortInfo?.value];
						bValue = b[sortInfo?.value];
					}
				}
			} else {
				const dataKey = (colMapping && colMapping[sortColumn]) || sortColumn;
				aValue = a[dataKey];
				bValue = b[dataKey];
				if (typeof aValue === 'number' && typeof bValue === 'number') {
					sortType = 'number';
				}
			}

			if (sortType === 'number') {
				const aNum = parseFloat(aValue) || 0;
				const bNum = parseFloat(bValue) || 0;
				return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
			}

			const aStr = String(aValue || '').toLowerCase();
			const bStr = String(bValue || '').toLowerCase();
			return sortDirection === 'asc'
				? aStr.localeCompare(bStr)
				: bStr.localeCompare(aStr);
		});

		return [...fixedRows, ...sortedNonFixed];
	}, [activeData, sortColumn, sortDirection, colMapping, customRowData, sortValueInfo]);

	const rowVirtualization = useVirtulization({
		data: sortedData,
		rowHeight: 50,
		scrollContainerRef,
		visibleCount: 10,
		overscan: 5,
		enabled: virtualization,
	});

	const renderCell = (product, column, index, absoluteIndex) => {
		return customRowData[column?.key]
			? customRowData[column?.key](product, index, column?.key, absoluteIndex) ?? '-'
			: product[colMapping?.[column?.key] ?? column?.key];
	};

	const getFixedColumnOffset = (columnIndex) => {
		let offset = 0;
		for (let i = 0; i < columnIndex; i++) {
			if (columns[i]?.fixed) {
				offset += columns[i]?.width || 200;
			}
		}
		return offset;
	};

	const getLastFixedColumnIndex = () => {
		for (let i = columns.length - 1; i >= 0; i--) {
			if (columns[i]?.fixed) {
				return i;
			}
		}
		return -1;
	};

	const handleScroll = useCallback(() => {
		if (scrollContainerRef.current) {
			const { scrollLeft } = scrollContainerRef.current;
			setIsScrolled(scrollLeft > 0);
		}
	}, []);

	return (
		<div
			ref={scrollContainerRef}
			className='h-full w-full overflow-auto relative'
			onScroll={handleScroll}
		>
			{isLoading && (
				<div className='absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50'>
					<div className='flex flex-col items-center gap-2'>
						<Loader2 className='w-8 h-8 text-yellow-500 animate-spin' />
						<span className='text-sm text-gray-600'>Loading...</span>
					</div>
				</div>
			)}
			<table className='w-full'>
				<thead ref={theadRef} className='bg-gray-50'>
					{hasGroups && (
						<tr ref={groupRowRef} className={headerRowClassName}>
							{groupSegments.map((segment, segIndex) => {
								const isFixedSegment = segment.fixed;
								const isLastSegment = segIndex === groupSegments.length - 1;
								// the fixed group that ends at the last pinned column gets the scroll shadow
								const isLastFixedSegment =
									isFixedSegment && segment.endIndex === getLastFixedColumnIndex();
								return (
									<th
										key={`group-${segment.label ?? 'none'}-${segment.startIndex}`}
										colSpan={segment.colSpan}
										className={`relative py-2.5 px-3 text-[11px] font-bold uppercase tracking-wider text-center border-b-2 border-gray-300 ${isLastSegment ? '' : 'border-r border-gray-300'} sticky top-0 ${isFixedSegment ? 'z-50' : 'z-40'}`}
										style={{
											...(isFixedSegment
												? { left: `${getFixedColumnOffset(segment.startIndex)}px` }
												: {}),
											background: segment.color || undefined,
											color: segment.textColor || undefined,
										}}
									>
										{segment.subLabel ? (
											<div className='flex flex-col items-center gap-0.5'>
												<span>{segment.label || ''}</span>
												<span className='whitespace-nowrap rounded px-1.5 py-[1px] text-[10px] font-bold normal-case tracking-normal bg-blue-100 text-blue-700'>
													{segment.subLabel}
												</span>
											</div>
										) : (segment.label || '')}
										{isLastFixedSegment && isScrolled && (
											<div
												style={{
													position: 'absolute',
													top: 0,
													right: 0,
													width: '8px',
													height: '100%',
													pointerEvents: 'none',
													boxShadow: '4px 0 6px -2px rgb(243, 220, 16)',
													zIndex: 1,
												}}
											/>
										)}
									</th>
								);
							})}
							{showSubRow && (
								<th
									className='border-b-2 border-gray-300 sticky top-0 z-40'
									style={{ width: '60px', minWidth: '60px' }}
								/>
							)}
						</tr>
					)}
					<tr className={headerRowClassName}>
						{columns.map((column, index) => {
							const isLastFixed = index === getLastFixedColumnIndex();
							const isFixedColumn = column.fixed;
							return (
								<th
									key={column.key}
									className={`py-3 gap-2 text-left px-1 text-[14px] ${headerCellClassName} font-medium text-gray-700 border-b border-gray-300 ${hasGroups && groupBoundaryIndices.has(index) ? 'border-r border-gray-300' : ''} sticky ${isFixedColumn ? 'z-50' : 'z-40'}`}
									style={{
										top: hasGroups ? `${groupHeaderHeight}px` : 0,
										...(isFixedColumn ? { left: `${getFixedColumnOffset(index)}px` } : {}),
										width: column.width ? `${column.width}px` : '200px',
										minWidth: column.width ? `${column.width}px` : '200px',
									}}
									onClick={() => handleSort(column)}
								>
									<div className={`flex items-start justify-start gap-2 w-full`}>
										{column?.customCol ? customCol(column) : column.title}
										{column?.sortable && getSortIcon(column)}
									</div>
									{isLastFixed && isScrolled && (
										<div
											style={{
												position: 'absolute',
												top: 0,
												right: 0,
												width: '8px',
												height: '100%',
												pointerEvents: 'none',
												boxShadow: '4px 0 6px -2px rgb(243, 220, 16)',
												zIndex: 1
											}}
										/>
									)}
								</th>
							);
						})}
						{showSubRow && (
							<th
								className={`px-4 text-left text-sm font-medium text-gray-700 border-b border-gray-300 sticky z-40 ${headerCellClassName}`}
								style={{ top: hasGroups ? `${groupHeaderHeight}px` : 0, width: '60px', minWidth: '60px' }}
							>
								View
							</th>
						)}
					</tr>
				</thead>
				<tbody className='divide-y divide-gray-200'>
					{virtualization && rowVirtualization.paddingTop > 0 && (
						<tr>
							<td colSpan={columns.length} style={{ height: rowVirtualization.paddingTop }} />
						</tr>
					)}
					{sortedData?.length > 0 ? (
						(virtualization ? rowVirtualization.virtualRows : sortedData).map((product, index) => {
							const absoluteIndex = virtualization
								? rowVirtualization.startIndex + index
								: index;
							const isFixedRow = product?.fixed;
							const rowKey = product.rowKey ?? (product.id || absoluteIndex);
							const isExpanded = !!expandedSubRow[rowKey];

							return (
								<React.Fragment key={rowKey}>
									<tr
										key={`main-${rowKey}`}
										className={`hover:bg-gray-50 ${highLightRow == index ? 'bg-yellow-100' : ''} ${isFixedRow ? 'sticky z-20 bg-yellow-500' : ''} ${product?.rowClassName ?? ''}`}
										style={{
											...(isFixedRow ? { top: `${headerHeight}px` } : {}),
											...(product?.rowStyle ?? {}),
										}}
										{...(product?.onClick && { onClick: () => product?.onClick(product) })}
									>
										{columns?.map((column, colIndex) => {
											const isLastFixed = colIndex === getLastFixedColumnIndex();
											const isFixedColumn = column?.fixed;
											const isColoured = column?.coloured;
											return (
												<td
													key={column.key}
													className={`text-left px-1  text-sm border-b border-gray-200 ${hasGroups && groupBoundaryIndices.has(colIndex) ? 'border-r border-gray-200' : ''} ${isFixedColumn
														? `sticky ${isColoured ? 'bg-slate-100' : 'bg-white'} ${isFixedRow ? 'bg-yellow-500' : ''}`
														: ''
														} ${isColoured ? 'bg-slate-100' : ''} ${isFixedRow ? 'bg-yellow-500' : ''}`}
													style={{
														...(isFixedColumn ? { left: `${getFixedColumnOffset(colIndex)}px` } : {}),
														width: column.width ? `${column.width}px` : '200px',
														minWidth: column.width ? `${column.width}px` : '200px',
														zIndex: isFixedColumn ? 10 : 1,
														// Sticky cells have an opaque bg (bg-white) that hides the
														// row tint — carry the row's backgroundColor onto them.
														...(isFixedColumn && product?.rowStyle?.backgroundColor
															? { backgroundColor: product.rowStyle.backgroundColor }
															: {}),
													}}
												>
													{renderCell(product, column, index, absoluteIndex)}
													{isLastFixed && isScrolled && (
														<div
															style={{
																position: 'absolute',
																top: 0,
																right: 0,
																width: '8px',
																height: '100%',
																pointerEvents: 'none',
																boxShadow: '4px 0 6px -2px rgb(243, 220, 16)',
																zIndex: 1
															}}
														/>
													)}
												</td>
											);
										})}
										{showSubRow && (
											<td className='px-4 text-left text-sm border-b border-gray-200'>
												<button
													onClick={(e) => {
														e.stopPropagation();
														setExpandedSubRow((prev) => ({
															...prev,
															[rowKey]: !prev[rowKey],
														}));
													}}
													className="p-1 hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center w-full"
												>
													{isExpanded ? (
														<Minus size={18} className="text-gray-500" />
													) : (
														<Plus size={18} className="text-gray-500" />
													)}
												</button>
											</td>
										)}
									</tr>
									{isExpanded && (
										<tr key={`sub-${rowKey}`} className='bg-gray-50 shadow-inner'>
											<td colSpan={columns.length + (showSubRow ? 1 : 0)} className="p-0">
												{renderSubRow(product, index)}
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})
					) : (
						<tr>
							<td
								colSpan={columns.length + (showSubRow ? 1 : 0)}
								className='px-4 py-6 text-center text-sm text-gray-500'
							>
								{customInfoLine}
							</td>
						</tr>
					)}
					{virtualization && rowVirtualization.paddingBottom > 0 && (
						<tr>
							<td colSpan={columns.length + (showSubRow ? 1 : 0)} style={{ height: rowVirtualization.paddingBottom }} />
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
}

export default VariantTable;